import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  Share as ShareIcon,
  Assessment as AnalyticsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

function PublishedForms({ darkMode }) {
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);

  useEffect(() => {
    loadPublishedForms();
  }, []);

  const loadPublishedForms = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/forms/published', {
        method: 'GET',
        credentials: 'include',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('tkn')}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setForms(data.forms || []);
      } else {
        setError('Failed to load published forms');
      }
    } catch (error) {
      console.error('Error loading forms:', error);
      setError('Error loading forms');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, form) => {
    setAnchorEl(event.currentTarget);
    setSelectedForm(form);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedForm(null);
  };

  const handleViewForm = (form) => {
    navigate(`/form/${form._id}`);
    handleMenuClose();
  };

  const handleEditForm = (form) => {
    navigate(`/formBuilder?load=${form._id}`);
    handleMenuClose();
  };

  const handleViewAnalytics = (form) => {
    navigate(`/form-analytics/${form._id}`);
    handleMenuClose();
  };

  const handleShareForm = (form) => {
    const shareUrl = `${window.location.origin}/form/${form._id}`;
    navigator.clipboard.writeText(shareUrl);
    // You could add a toast notification here
    console.log('Form URL copied to clipboard:', shareUrl);
    handleMenuClose();
  };

  const filteredForms = forms.filter((form) => form.name.toLowerCase().includes(searchTerm.toLowerCase())
    || (form.description && form.description.toLowerCase().includes(searchTerm.toLowerCase())));

  if (loading) {
    return (
      <Box sx={{
        display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh',
      }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{
        mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}
      >
        <Typography variant="h4" component="h1">
          Published Forms
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/formBuilder')}
          sx={{ ml: 2 }}
        >
          Create New Form
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search forms..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 400 }}
        />
      </Box>

      {filteredForms.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {searchTerm ? 'No forms found matching your search' : 'No published forms found'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {!searchTerm && 'Create your first form to get started'}
          </Typography>
          {!searchTerm && (
            <Button
              variant="contained"
              onClick={() => navigate('/formBuilder')}
              sx={{ mt: 2 }}
            >
              Create Form
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredForms.map((form) => (
            <Grid item xs={12} sm={6} md={4} key={form._id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1,
                  }}
                  >
                    <Typography variant="h6" component="h2" sx={{ flexGrow: 1, mr: 1 }}>
                      {form.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, form)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>

                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Chip
                      label={form.status || 'published'}
                      color="success"
                      size="small"
                    />
                    {form.category && (
                      <Chip
                        label={form.category}
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </Stack>

                  {form.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {form.description}
                    </Typography>
                  )}

                  <Typography variant="caption" color="text.secondary">
                    Created:
                    {' '}
                    {new Date(form.createdAt).toLocaleDateString()}
                  </Typography>
                  {form.submissionCount && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {form.submissionCount}
                      {' '}
                      submissions
                    </Typography>
                  )}
                </CardContent>

                <CardActions>
                  <Button
                    size="small"
                    startIcon={<ViewIcon />}
                    onClick={() => handleViewForm(form)}
                  >
                    View
                  </Button>
                  <Button
                    size="small"
                    startIcon={<AnalyticsIcon />}
                    onClick={() => handleViewAnalytics(form)}
                  >
                    Analytics
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleViewForm(selectedForm)}>
          <ViewIcon sx={{ mr: 1 }} fontSize="small" />
          View Form
        </MenuItem>
        <MenuItem onClick={() => handleEditForm(selectedForm)}>
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit Form
        </MenuItem>
        <MenuItem onClick={() => handleViewAnalytics(selectedForm)}>
          <AnalyticsIcon sx={{ mr: 1 }} fontSize="small" />
          View Analytics
        </MenuItem>
        <MenuItem onClick={() => handleShareForm(selectedForm)}>
          <ShareIcon sx={{ mr: 1 }} fontSize="small" />
          Copy Share Link
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default PublishedForms;
